import { useState } from "react";

type Props = {
  selectOptions?: any;
  name?: string;
  opt?: string;
  totalPages?: number;
  fetchData?: (pageNo: number) => void;
  currentPage?: number;
};

export default function Pagination(props: Props) {
  const { fetchData, totalPages = 1, currentPage: propCurrentPage = 1 } = props;
  const [localCurrentPage, setLocalCurrentPage] = useState(propCurrentPage);

  // Sync local state with prop
  const currentPage = propCurrentPage !== undefined ? propCurrentPage : localCurrentPage;

  const handlePageNo = (pageNo: number) => {
    if (pageNo < 1 || pageNo > totalPages) return;
    if (propCurrentPage === undefined) {
      setLocalCurrentPage(pageNo);
    }
    fetchData?.(pageNo);
  };

  const renderPageNumbers = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      pages.push(
        <button
          key={1}
          className={`w-8 h-8 rounded-md text-sm font-medium border transition-all duration-150 cursor-pointer ${
            currentPage === 1
              ? "bg-[#FBD037] border-[#FBD037]"
              : "bg-white border-[#F1F1F1] hover:border-[#FBD037]"
          }`}
          onClick={() => handlePageNo(1)}
        >
          1
        </button>
      );
    }

    if (startPage > 2) {
      pages.push(
        <span key="left-ellipsis" className="px-1">
          ...
        </span>
      );
    }

    for (let i = startPage; i <= endPage; i++) {
      if (i === 1 && startPage !== 1) continue;
      if (i === totalPages && endPage !== totalPages) continue;

      pages.push(
        <button
          key={i}
          className={`w-8 h-8 rounded-md text-sm font-medium border transition-all duration-150 cursor-pointer ${
            currentPage === i
              ? "bg-[#FBD037] border-[#FBD037]"
              : "bg-white border-[#F1F1F1] hover:border-[#FBD037]"
          }`}
          onClick={() => handlePageNo(i)}
        >
          {i}
        </button>
      );
    }

    if (endPage < totalPages - 1) {
      pages.push(
        <span key="right-ellipsis" className="px-1">
          ...
        </span>
      );
    }

    if (endPage < totalPages) {
      pages.push(
        <button
          key={totalPages}
          className={`w-8 h-8 rounded-md text-sm font-medium border transition-all duration-150 cursor-pointer ${
            currentPage === totalPages
              ? "bg-[#FBD037] border-[#FBD037]"
              : "bg-white border-[#F1F1F1] hover:border-[#FBD037]"
          }`}
          onClick={() => handlePageNo(totalPages)}
        >
          {totalPages}
        </button>
      );
    }

    return pages;
  };

  if (totalPages <= 0) return null;

  return (
    <div className="flex justify-end py-2 px-10">
      <div className="space-x-2 flex items-center">
        <button
          className="bg-white text-sm px-3 rounded-md border border-[#F1F1F1] font-medium h-8 transition-all duration-150 hover:border-[#FBD037] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={() => handlePageNo(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Prev
        </button>

        {renderPageNumbers()}

        <button
          className="bg-white text-sm px-3 rounded-md border border-[#F1F1F1] font-medium h-8 transition-all duration-150 hover:border-[#FBD037] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={() => handlePageNo(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
}